/**
 * InboxNotices — what the school has told this student.
 *
 * Chiefly the weekly note about lessons their class covered without them, and
 * which of those can be watched back. Notices were being written to the
 * database with nothing to read them; this is that.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Mail, MailOpen } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof body.detail === 'string' ? body.detail : 'Request failed');
  return body;
};

export const InboxNotices = ({ tenantId }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const inbox = useQuery({
    queryKey: ['school-inbox', tenantId],
    queryFn: () => api(`/school/tenants/${tenantId}/inbox`),
    retry: false,
  });

  const markRead = useMutation({
    mutationFn: (id) => api(`/school/tenants/${tenantId}/inbox/${id}/read`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['school-inbox', tenantId] }),
    onError: (e) => toast.error(e.message || 'That notice could not be cleared'),
  });

  const rows = (inbox.data?.notices || []).filter((n) => !n.is_read);
  if (inbox.isError || rows.length === 0) return null;

  return (
    <Card className="p-4 mb-4 border-indigo-500/30" data-testid="inbox-notices">
      <div className="flex items-center gap-2 mb-2">
        <Mail className="h-4 w-4 text-indigo-600" />
        <p className="text-sm font-medium">From the school</p>
        <span className="text-xs text-slate-500 ml-auto">{inbox.data?.unread || 0} unread</span>
      </div>
      <ul className="space-y-1.5">
        {rows.map((n) => (
          <li key={n.id} className="border rounded-lg px-3 py-2" data-testid={`notice-${n.id}`}>
            <p className="text-sm font-medium">{n.title}</p>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">{n.body}</p>
            <div className="flex gap-2 mt-2">
              {n.deep_link && (
                <Button size="sm" variant="outline" onClick={() => navigate(n.deep_link)}
                        data-testid={`notice-open-${n.id}`}>
                  {n.replays_available > 0 ? 'Watch what you missed' : 'Open'}
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => markRead.mutate(n.id)}
                      data-testid={`notice-read-${n.id}`}>
                <MailOpen className="h-3.5 w-3.5 mr-1" /> Mark read
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
};

export default InboxNotices;
